import React, { useMemo, useState } from 'react';
import {
  Card,
  Spinner,
  Title2,
  Text,
  Caption1,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
} from '@fluentui/react-icons';
import { Link } from 'react-router';
import { useHealthCheckData } from '../../hooks/useHealthCheckData';
import type { DeploymentHealthEntry } from '../../services/healthcheck';
import { getErrorMessage } from '../../utils/errors';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px', padding: tokens.spacingVerticalL,
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalL },
  card: { padding: tokens.spacingVerticalM },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS },
  healthRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, marginBottom: '2px' },
  section: { marginBottom: tokens.spacingVerticalL },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalS },
  issueGroup: {
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalXS,
    overflow: 'hidden',
  },
  issueHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer', userSelect: 'none' as const,
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: '36px',
  },
  issueBody: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: `2px ${tokens.spacingHorizontalM}`,
  },
  depItem: { fontSize: tokens.fontSizeBase200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  focusableButton: {
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusMedium,
    },
  },
  pathSection: {
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalXS,
    overflow: 'hidden',
  },
  pathHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer', userSelect: 'none' as const,
    minHeight: '36px',
  },
  pathBody: {
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: `4px ${tokens.spacingHorizontalM}`,
  },
});

/** Classify error text into a short category */
function classifyError(err: string | undefined): string {
  if (!err) return 'Unknown Error';
  const lower = err.toLowerCase();
  if (lower.includes('permissiondenied') || lower.includes('401')) return 'Permission Denied (401)';
  if (lower.includes('404') || lower.includes('not found') || lower.includes('resource not found')) return 'Not Found (404)';
  if (lower.includes('429') || lower.includes('rate limit') || lower.includes('throttl')) return 'Rate Limited (429)';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'Timeout';
  if (lower.includes('unreachable') || lower.includes('connection') || lower.includes('econnrefused')) return 'Endpoint Unreachable';
  if (lower.includes('500') || lower.includes('internal server')) return 'Server Error (500)';
  if (lower.includes('503') || lower.includes('service unavailable')) return 'Service Unavailable (503)';
  if (lower.startsWith('url:') || lower.startsWith('http://') || lower.startsWith('https://')) return 'Endpoint Unreachable';
  // Use full first line — CSS handles overflow
  return err.split('\n')[0] || 'Unknown Error';
}

const AdminDashboard: React.FC = () => {
  const styles = useStyles();

  const { data: healthData, isLoading: healthLoading, isError: healthError, error: healthErrorObj, dataUpdatedAt: healthUpdatedAt } = useHealthCheckData();

  // Compute detailed health data
  const healthDetails = useMemo(() => {
    if (!healthData?.summary) return { total: 0, healthy: 0, unhealthy: 0, paths: [] as PathHealth[], errorGroups: [] as ErrorGroup[], totalDeps: 0, healthyDeps: 0 };

    type FailedDep = { apiPath: string; deployment: string; error: string; capability: string };
    type PathHealth = { path: string; healthy: number; total: number; failures: FailedDep[] };
    type ErrorGroup = { category: string; count: number; deployments: FailedDep[] };

    const paths: PathHealth[] = [];
    const allFailures: FailedDep[] = [];
    let totalDeps = 0, healthyDeps = 0;

    for (const [key, val] of Object.entries(healthData.summary)) {
      if (key.startsWith('/')) continue;
      const depHealth = healthData.deploymentHealth?.[key] ?? {};
      const failures: FailedDep[] = [];
      let pathTotal = 0, pathHealthy = 0;

      for (const [depName, entries] of Object.entries(depHealth)) {
        pathTotal++;
        const allOk = entries.every((e: DeploymentHealthEntry) => e.status === 'success');
        if (allOk) {
          pathHealthy++;
        } else {
          const failedEntries = entries.filter((e: DeploymentHealthEntry) => e.status !== 'success');
          for (const fe of failedEntries) {
            failures.push({ apiPath: key, deployment: depName, error: fe.error || '', capability: fe.capability });
          }
        }
      }
      totalDeps += pathTotal;
      healthyDeps += pathHealthy;
      paths.push({ path: key, healthy: pathHealthy, total: pathTotal, failures });
      allFailures.push(...failures);
    }

    paths.sort((a, b) => a.path.localeCompare(b.path));

    // Group failures by error category
    const groupMap = new Map<string, FailedDep[]>();
    for (const f of allFailures) {
      const cat = classifyError(f.error);
      if (!groupMap.has(cat)) groupMap.set(cat, []);
      groupMap.get(cat)!.push(f);
    }
    const errorGroups = Array.from(groupMap.entries())
      .map(([category, deployments]) => ({ category, count: deployments.length, deployments }))
      .sort((a, b) => b.count - a.count);

    const healthy = paths.filter(p => p.healthy === p.total && p.total > 0).length;
    const unhealthy = paths.filter(p => p.healthy < p.total && p.total > 0).length;

    return { total: paths.length, healthy, unhealthy, paths, errorGroups, totalDeps, healthyDeps };
  }, [healthData]);


  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const toggleError = (cat: string) => setExpandedErrors(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });
  const togglePath = (path: string) => setExpandedPaths(prev => { const n = new Set(prev); n.has(path) ? n.delete(path) : n.add(path); return n; });

  const loading = healthLoading;

  return (
    <div className={styles.container}>
      <Title2>Service Health</Title2>
      <Caption1 style={{ display: 'block', marginBottom: tokens.spacingVerticalM }}>
        Real-time health status from TRAPI health check reports.
        {healthData?.updatedAt && <> Last updated: {new Date(healthData.updatedAt).toLocaleString()}</>}
        {!healthData?.updatedAt && healthUpdatedAt > 0 && <> Data fetched: {new Date(healthUpdatedAt).toLocaleTimeString()}</>}
      </Caption1>

      {/* Error banners */}
      {healthError && (
        <div role="alert" style={{ padding: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalM, backgroundColor: tokens.colorPaletteRedBackground1, borderRadius: tokens.borderRadiusMedium, display: 'flex', alignItems: 'center', gap: 8 }}>
          <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
          <Text size={200}>Health data unavailable: {getErrorMessage(healthErrorObj)}</Text>
        </div>
      )}

      {/* Summary cards */}
      <div className={styles.grid}>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Text weight="semibold">API Paths</Text>
            <Link to="/admin/apis"><Caption1>Manage →</Caption1></Link>
          </div>
          {loading ? <Spinner size="tiny" /> : (
            <>
              <Text size={800} weight="bold">{healthDetails.total}</Text>
              <div className={styles.healthRow}>
                <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                <Caption1>{healthDetails.healthy} fully healthy</Caption1>
              </div>
              {healthDetails.unhealthy > 0 && (
                <div className={styles.healthRow}>
                  <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                  <Caption1>{healthDetails.unhealthy} with issues</Caption1>
                </div>
              )}
            </>
          )}
        </Card>

        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Text weight="semibold">Deployments</Text>
            <Link to="/admin/backends"><Caption1>Manage →</Caption1></Link>
          </div>
          {loading ? <Spinner size="tiny" /> : (
            <>
              <Text size={800} weight="bold">{healthDetails.totalDeps}</Text>
              <div className={styles.healthRow}>
                <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                <Caption1>{healthDetails.healthyDeps} healthy</Caption1>
              </div>
              {healthDetails.totalDeps - healthDetails.healthyDeps > 0 && (
                <div className={styles.healthRow}>
                  <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                  <Caption1>{healthDetails.totalDeps - healthDetails.healthyDeps} unhealthy</Caption1>
                </div>
              )}
            </>
          )}
        </Card>

        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <Text weight="semibold">Tasks</Text>
            <Link to="/admin/tasks"><Caption1>Run →</Caption1></Link>
          </div>
          <Caption1 style={{ display: 'block' }}>
            <Link to="/admin/tasks?task=capabilities-testing">Capabilities</Link>{' · '}
            <Link to="/admin/tasks?task=apipath-testing">API Paths</Link>{' · '}
            <Link to="/admin/tasks?task=example-testing">Examples</Link>{' · '}
            <Link to="/admin/tasks?task=failure-testing">Failures</Link>
          </Caption1>
        </Card>
      </div>

      {/* Issues by Error Type — aggregated, expandable */}
      {!loading && healthDetails.errorGroups.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text weight="semibold" size={400}>Issues by Error Type</Text>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {healthDetails.errorGroups.reduce((s, g) => s + g.count, 0)} total failures across {healthDetails.errorGroups.length} categories
            </Caption1>
          </div>
          {healthDetails.errorGroups.map(g => {
            const isOpen = expandedErrors.has(g.category);
            // Dedupe by deployment name for display
            const uniqueDeps = Array.from(new Set(g.deployments.map(d => d.deployment))).sort();
            return (
              <div key={g.category} className={styles.issueGroup}>
                <div className={`${styles.issueHeader} ${styles.focusableButton}`} onClick={() => toggleError(g.category)}
                  role="button" tabIndex={0} style={{ outline: 'none' }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleError(g.category); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 10, width: 12, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                    <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }} />
                    <Text weight="semibold" size={200} title={g.category} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.category}</Text>
                  </div>
                  <Badge appearance="tint" color="danger" size="small" style={{ flexShrink: 0, marginLeft: 8 }}>{g.count} failure{g.count !== 1 ? 's' : ''}</Badge>
                </div>
                {isOpen && (
                  <div className={styles.issueBody}>
                    {uniqueDeps.map(dep => (
                      <Caption1 key={dep} className={styles.depItem} title={dep}>{dep}</Caption1>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Health by API Path — expandable to show failures */}
      {!loading && healthDetails.paths.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text weight="semibold" size={400}>Health by API Path</Text>
          </div>
          {healthDetails.paths.map(p => {
            const allHealthy = p.healthy === p.total && p.total > 0;
            const isOpen = expandedPaths.has(p.path);
            const hasFails = p.failures.length > 0;
            return (
              <div key={p.path} className={styles.pathSection}>
                <div
                  className={`${styles.pathHeader} ${hasFails ? styles.focusableButton : ''}`}
                  onClick={() => hasFails && togglePath(p.path)}
                  style={{ cursor: hasFails ? 'pointer' : 'default', outline: 'none' }}
                  role={hasFails ? 'button' : undefined}
                  tabIndex={hasFails ? 0 : undefined}
                  onKeyDown={e => { if (hasFails && (e.key === 'Enter' || e.key === ' ')) togglePath(p.path); }}
                >
                  {hasFails && <span style={{ fontSize: 10, width: 12 }}>{isOpen ? '▾' : '▸'}</span>}
                  {allHealthy ? (
                    <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteGreenForeground1, flexShrink: 0 }} />
                  ) : (
                    <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }} />
                  )}
                  <Text size={200} weight="semibold">{p.path}</Text>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                    {p.healthy}/{p.total}
                  </Caption1>
                </div>
                {isOpen && hasFails && (
                  <div style={{ padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}` }}>
                    {p.failures.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', borderBottom: i < p.failures.length - 1 ? `1px solid ${tokens.colorNeutralStroke3}` : undefined }}>
                        <Caption1 style={{ fontWeight: 600, minWidth: 180, flexShrink: 0 }}>{f.deployment}</Caption1>
                        <Caption1 style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }}>{f.capability}</Caption1>
                        <Caption1
                          title={f.error}
                          style={{
                            color: tokens.colorPaletteRedForeground1,
                            flex: 1, minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {f.error || '—'}
                        </Caption1>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
